import { getCompanyCoordinates } from './geo'
import { getCompanyPlan } from './VerifiedBadge'
import { formatServiceTag, getServiceTagsText, parseServiceTags, serviceTagKey } from './serviceTags'

// Shared by the web and mobile interfaces. No storage, authentication or network
// calls belong here: identical companies and `now` must produce identical results.
export const opportunityLimits = Object.freeze({ starter: 3, basic: 30, premium: Infinity })
const planPriority = { premium: 0, basic: 1, starter: 2 }
const normalizePlan = value => {
  const plan = String(value || '').toLowerCase()
  return plan.includes('premium') ? 'premium' : plan.includes('basic') ? 'basic' : 'starter'
}
export const getOpportunityLimit = plan => opportunityLimits[normalizePlan(plan)]

const swissDate = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Zurich', year: 'numeric', month: '2-digit', day: '2-digit',
})
const optionalDate = value => value == null || value === '' ? null : String(value).trim() || null
const tagExpiry = tag => optionalDate(tag?.expires) || optionalDate(tag?.expires_at) || optionalDate(tag?.until) || optionalDate(tag?.end)

export function parseCompanyNeedTags(value) {
  let tags = value
  if (typeof tags === 'string') {
    const raw = tags.trim()
    if (!raw) return []
    try { tags = JSON.parse(raw) } catch {
      // A legacy text need is valid; malformed serialized data is not a need.
      if (/^[\[{]/.test(raw)) return []
      tags = [raw]
    }
  }
  if (typeof tags === 'string') tags = [tags]
  if (!Array.isArray(tags)) return []
  return tags.flatMap(tag => {
    const source = typeof tag === 'string' ? { label: tag } : tag
    if (!source || typeof source !== 'object' || Array.isArray(source)) return []
    const label = (typeof source.label === 'string' ? source.label.trim() : '')
      || (typeof source.title === 'string' ? source.title.trim() : '')
    if (!label) return []
    return [{ ...source, label, starts: optionalDate(source.starts) || optionalDate(source.starts_at) || optionalDate(source.start), expires: tagExpiry(source) }]
  })
}

export function isNeedExpired(value, now = Date.now()) {
  const expires = value && typeof value === 'object' && !(value instanceof Date)
    ? tagExpiry(value) : value
  if (expires == null || expires === '') return false
  const deadline = expires instanceof Date ? expires : String(expires).trim()
  if (deadline === '') return false
  const currentTime = new Date(now).getTime()
  if (!Number.isFinite(currentTime)) return true
  if (typeof deadline === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(deadline)) {
    const parsed = new Date(`${deadline}T00:00:00.000Z`)
    if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== deadline) return true
    const parts = Object.fromEntries(swissDate.formatToParts(new Date(currentTime)).map(part => [part.type, part.value]))
    return deadline < `${parts.year}-${parts.month}-${parts.day}`
  }
  // Legacy timestamps without an offset are interpreted as UTC everywhere,
  // rather than in each visitor's device timezone.
  const timestamp = typeof deadline === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/.test(deadline)
    ? `${deadline}Z` : deadline
  const endTime = new Date(timestamp).getTime()
  return !Number.isFinite(endTime) || endTime <= currentTime
}

export const getActiveNeedTags = (value, now = Date.now()) =>
  parseCompanyNeedTags(value).filter(tag => !isNeedExpired(tag, now))

export function getPermanentNeedText(company, now = Date.now()) {
  if (isNeedExpired(company?.needs_expires_at, now)) return ''
  return String(company?.needs_description || '').trim()
}

const stopWords = new Set([
  'avec', 'dans', 'pour', 'des', 'les', 'une', 'sur', 'nous', 'vous', 'notre', 'votre', 'besoin',
  'besoins', 'entreprise', 'entreprises', 'service', 'services', 'projet', 'projets', 'recherche',
  'partenaire', 'partenaires', 'suisse', 'local', 'locaux', 'faire', 'plus', 'aux', 'par',
  'commun', 'commune', 'communs', 'communes',
])
const sectorServiceKeywords = {
  'Marketing & Publicité': [
    'marketing', 'publicité', 'communication', 'campagne', 'réseaux sociaux', 'branding',
    'identité visuelle', 'logo', 'graphisme', 'design', 'print', 'impression', 'flyer',
    'affiche', 'signalétique', 'enseigne', 'site web', 'digital', 'publicitaire',
  ],
  'Marketing Digital': ['marketing', 'digital', 'réseaux sociaux', 'site web', 'campagne', 'seo', 'publicité', 'communication'],
  'Design & Créatif': ['design', 'graphisme', 'logo', 'branding', 'identité visuelle', 'illustration', 'création', 'visuel'],
  'Design & Communication': ['design', 'communication', 'graphisme', 'logo', 'branding', 'identité visuelle', 'supports', 'visuel'],
  'Informatique & Tech': ['informatique', 'logiciel', 'application', 'site web', 'développement', 'tech', 'maintenance', 'automatisation'],
  Informatique: ['informatique', 'logiciel', 'application', 'site web', 'développement', 'maintenance', 'support'],
  'Fiduciaire & Comptabilité': ['fiduciaire', 'comptabilité', 'fiscalité', 'déclaration', 'salaires', 'bouclement', 'audit'],
  Fiduciaire: ['fiduciaire', 'comptabilité', 'fiscalité', 'déclaration', 'salaires', 'bouclement'],
  'BTP & Construction': ['chantier', 'construction', 'rénovation', 'maçonnerie', 'peinture', 'plomberie', 'électricité', 'architecture'],
  Construction: ['chantier', 'construction', 'rénovation', 'maçonnerie', 'peinture', 'plomberie', 'électricité'],
  'Ressources Humaines': ['recrutement', 'rh', 'formation', 'coaching', 'emploi', 'talent', 'salaire'],
  'Transport & Logistique': ['transport', 'livraison', 'logistique', 'stockage', 'déménagement', 'expédition'],
  'Commerce & Retail': ['commerce', 'vente', 'boutique', 'retail', 'distribution', 'fournisseur'],
  Immobilier: ['immobilier', 'location', 'vente', 'gestion', 'courtage', 'estimation'],
  'Finance & Assurance': ['finance', 'assurance', 'crédit', 'hypothèque', 'placement', 'prévoyance'],
  'Santé & Bien-être': ['santé', 'bien-être', 'soins', 'thérapie', 'coaching', 'médical'],
  'Éducation & Formation': ['formation', 'cours', 'éducation', 'atelier', 'apprentissage', 'coaching'],
  Juridique: ['juridique', 'avocat', 'contrat', 'droit', 'conseil', 'litige'],
  'Industrie & Production': ['industrie', 'production', 'usinage', 'fabrication', 'maintenance', 'atelier'],
  'Tourisme & Hôtellerie': ['tourisme', 'hôtel', 'hébergement', 'voyage', 'accueil', 'événement'],
  Restauration: ['restaurant', 'traiteur', 'cuisine', 'repas', 'événement', 'alimentation'],
  'Nettoyage & Facility': ['nettoyage', 'facility', 'entretien', 'maintenance', 'désinfection', 'jardin', 'jardinier', 'paysagiste', 'espaces verts', 'conciergerie'],
  Sécurité: ['sécurité', 'surveillance', 'alarme', 'contrôle', 'protection'],
  Événementiel: ['événement', 'animation', 'organisation', 'stand', 'sonorisation', 'location'],
  Consulting: ['conseil', 'stratégie', 'organisation', 'audit', 'accompagnement', 'optimisation'],
}
const normalizeText = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
const compareText = (a, b) => {
  const left = normalizeText(a), right = normalizeText(b)
  return left < right ? -1 : left > right ? 1 : 0
}
const unique = values => [...new Set(values.filter(Boolean))]
const tokenize = value => normalizeText(value).replace(/[^a-z0-9\s]/g, ' ').split(/\s+/)
  .filter(token => token.length >= 4 && !stopWords.has(token))
const roots = token => {
  const singular = token.length >= 7 && token.endsWith('s') ? token.slice(0, -1) : token
  return unique([token, singular, singular.length >= 7 ? singular.slice(0, 7) : ''])
}
const needKey = label => normalizeText(label).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'general'
const summary = text => {
  const firstSentence = text.split(/[.!?\n]/).find(part => part.trim()) || text
  const title = firstSentence.trim()
  return title.length > 110 ? `${title.slice(0, 107)}...` : title
}
const getFreshness = company => {
  const value = company?.needs_updated_at || company?.updated_at || company?.created_at
  const time = value ? new Date(value).getTime() : 0
  return Number.isFinite(time) ? time : 0
}
const distanceBetween = (viewer, company) => {
  const a = getCompanyCoordinates(viewer), b = getCompanyCoordinates(company)
  if (!a || !b) return null
  const toRad = degrees => degrees * Math.PI / 180
  const value = Math.sin(toRad(b.lat - a.lat) / 2) ** 2
    + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(toRad(b.lng - a.lng) / 2) ** 2
  const bounded = Math.max(0, Math.min(1, value))
  return 6371 * 2 * Math.atan2(Math.sqrt(bounded), Math.sqrt(1 - bounded))
}
const isSuspended = company => company?.is_suspended === true || company?.is_suspended === 'true'

export function buildNeedOpportunities(viewer, candidates, { now = Date.now() } = {}) {
  if (!viewer || isSuspended(viewer)) return []
  const currentTime = new Date(now).getTime()
  if (!Number.isFinite(currentTime)) return []
  const sector = Object.keys(sectorServiceKeywords).find(key => normalizeText(key) === normalizeText(viewer.sector))
  const offerText = [viewer.sector, viewer.description, getServiceTagsText(viewer.service_tags), ...(sectorServiceKeywords[sector] || [])].filter(Boolean).join(' ')
  const offerRoots = new Set(tokenize(offerText).flatMap(roots))
  const services = parseServiceTags(viewer.service_tags)
  const entries = []
  for (const company of Array.isArray(candidates) ? candidates : []) {
    if (!company?.id || isSuspended(company) || company.id === viewer.id
      || (viewer.user_id && company.user_id === viewer.user_id)) continue
    const activeTags = getActiveNeedTags(company.needs_tags, currentTime)
    const permanent = getPermanentNeedText(company, currentTime)
    const needs = [
      ...(permanent ? [{ key: 'general', kind: 'permanent', label: permanent, text: permanent, starts: null, expires: optionalDate(company.needs_expires_at) }] : []),
      ...activeTags.map(tag => ({ ...tag, key: needKey(tag.label), kind: 'punctual', text: tag.label })),
    ]
    const matchedNeeds = needs.flatMap(need => {
      const keywordMatches = unique(tokenize(need.text).filter(token => roots(token).some(root => offerRoots.has(root)))).slice(0, 4)
      const needText = normalizeText(need.text)
      const exactServiceTagMatches = services.filter(tag => {
        const key = serviceTagKey(tag)
        return key && needText.includes(key)
      }).slice(0, 4)
      const relevanceScore = keywordMatches.length * 3 + exactServiceTagMatches.length * 6
      return relevanceScore ? [{ ...need, title: summary(need.label), relevanceScore, keywordMatches, exactServiceTagMatches }] : []
    }).sort((a, b) => b.relevanceScore - a.relevanceScore || compareText(a.label, b.label)
      || compareText(a.kind, b.kind) || compareText(a.key, b.key)
      || compareText(a.starts, b.starts) || compareText(a.expires, b.expires))
    if (!matchedNeeds.length) continue

    const best = matchedNeeds[0]
    const distanceKm = distanceBetween(viewer, company)
    const freshness = getFreshness(company)
    const matchReasons = []
    let score = best.relevanceScore
    if (company.sector && company.sector === viewer.sector) { score += 2; matchReasons.push(company.sector) }
    if (company.canton && company.canton === viewer.canton) { score += 4; matchReasons.push(company.canton) }
    matchReasons.push(...best.keywordMatches, ...best.exactServiceTagMatches.map(formatServiceTag))
    if (distanceKm !== null) score += distanceKm <= 25 ? 3 : distanceKm <= 50 ? 2 : distanceKm <= 100 ? 1 : 0
    if (freshness > currentTime - 1000 * 60 * 60 * 24 * 30) score += 1
    // An attached subscription (including an empty/inactive one) is authoritative.
    const fallbackPlan = company.subscriptions == null ? normalizePlan(company.subscription_plan || company.plan) : 'starter'
    entries.push({
      id: company.id, company, title: best.title, activeTags, matchedNeeds, score,
      relevanceScore: best.relevanceScore, planRank: planPriority[getCompanyPlan(company, fallbackPlan)],
      distanceKm, freshness, matchReasons: unique(matchReasons).slice(0, 4),
      keywordMatches: best.keywordMatches, exactServiceTagMatches: best.exactServiceTagMatches,
    })
  }
  return entries.sort((a, b) => b.score - a.score || a.planRank - b.planRank || b.freshness - a.freshness
    || compareText(a.company.name, b.company.name) || compareText(a.id, b.id))
}
